import { useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowRight, CircleNotch } from "@phosphor-icons/react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { pulseContent } from "../data/pulseContent";

export const MembershipInquiryForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      full_name: "",
      phone: "",
      email: "",
      membership_plan: pulseContent.membershipPlans[0],
      fitness_goal: "",
      preferred_time: pulseContent.preferredTimes[4],
      message: "",
    },
  });

  const onSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const subject = encodeURIComponent(
        `Pulse Fitness Membership Inquiry — ${values.full_name}`,
      );
      const body = encodeURIComponent(
        [
          `Full Name: ${values.full_name}`,
          `Phone Number: ${values.phone}`,
          `Email Address: ${values.email}`,
          `Membership Interest: ${values.membership_plan}`,
          `Preferred Time: ${values.preferred_time}`,
          `Primary Fitness Goal: ${values.fitness_goal}`,
          `Additional Message: ${values.message || "N/A"}`,
        ].join("\n\n"),
      );

      window.location.href = `mailto:admin@pulsefitnesspk.com?subject=${subject}&body=${body}`;
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-6"
      data-testid="membership-inquiry-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label data-testid="membership-name-label" htmlFor="full_name">Full name</Label>
          <Input
            className="pulse-input"
            data-testid="membership-name-input"
            id="full_name"
            placeholder="Your full name"
            {...register("full_name", { required: "Full name is required" })}
          />
          {errors.full_name && <p className="form-error" data-testid="membership-name-error">{errors.full_name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label data-testid="membership-phone-label" htmlFor="phone">Phone number</Label>
          <Input
            className="pulse-input"
            data-testid="membership-phone-input"
            id="phone"
            placeholder="03XX XXX XXXX"
            {...register("phone", { required: "Phone number is required" })}
          />
          {errors.phone && <p className="form-error" data-testid="membership-phone-error">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label data-testid="membership-email-label" htmlFor="email">Email address</Label>
        <Input
          className="pulse-input"
          data-testid="membership-email-input"
          id="email"
          placeholder="you@example.com"
          type="email"
          {...register("email", { required: "Email is required" })}
        />
        {errors.email && <p className="form-error" data-testid="membership-email-error">{errors.email.message}</p>}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label data-testid="membership-plan-label" htmlFor="membership_plan">Membership interest</Label>
          <select
            className="pulse-select"
            data-testid="membership-plan-select"
            id="membership_plan"
            {...register("membership_plan", { required: true })}
          >
            {pulseContent.membershipPlans.map((plan) => (
              <option key={plan} value={plan}>{plan}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label data-testid="membership-time-label" htmlFor="preferred_time">Preferred time</Label>
          <select
            className="pulse-select"
            data-testid="membership-time-select"
            id="preferred_time"
            {...register("preferred_time", { required: true })}
          >
            {pulseContent.preferredTimes.map((time) => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label data-testid="membership-goal-label" htmlFor="fitness_goal">Primary fitness goal</Label>
        <Input
          className="pulse-input"
          data-testid="membership-goal-input"
          id="fitness_goal"
          placeholder="Strength, fat loss, mobility, or overall transformation"
          {...register("fitness_goal", { required: "Please share your fitness goal" })}
        />
        {errors.fitness_goal && <p className="form-error" data-testid="membership-goal-error">{errors.fitness_goal.message}</p>}
      </div>

      <div className="space-y-2">
        <Label data-testid="membership-message-label" htmlFor="message">Anything else we should know?</Label>
        <Textarea
          className="pulse-textarea"
          data-testid="membership-message-input"
          id="message"
          placeholder="Tell us about your current routine, goals, or preferred coaching style."
          rows={4}
          {...register("message")}
        />
      </div>

      <Button
        className="pulse-button-primary w-full justify-center gap-2 rounded-full py-6 text-sm"
        data-testid="membership-submit-button"
        disabled={isSubmitting}
        type="submit"
      >
        {isSubmitting ? (
          <>
            <CircleNotch className="h-5 w-5 animate-spin" />
            Sending inquiry
          </>
        ) : (
          <>
            Request membership
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </Button>
    </form>
  );
};